import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getCurrentUserId,
  getDealApplicationId,
  getDealBorrowerId,
  getHasAcknowledgedSpanishPrompt,
  getHasPreferredLanguageOverride,
  getLoanId,
  getLoanNumber,
  getNhDealApplicationId,
  getUserRole,
  selectPreferredLanguage,
  setHasAcknowledgedSpanishPrompt,
  setHasPreferredLanguageOverride,
  setPreferredLanguage,
} from "features/authSlice";
import { isPostLosLoan, selectHasLoans, selectIsLanguageToggleLocked } from "features/preferredLanguageSlice";
import PostLosLockModal from "./post-los-lock-modal/PostLosLockModal";
import LanguageToggle from "./language-toggle/LanguageToggle";
import EnglishRevertModal from "./english-revert-modal/EnglishRevertModal";
import SpanishAcknowledgmentModal from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { LoadingOverlay } from "components/ui/loading-spinners";
import { DEFAULT_LANGUAGE, LANG_PREF_COOKIE_NAME, LOCAL_STORAGE_KEY, SPANISH_LANG_CODE, SUPPORTED_LANGUAGES } from "utilities/Constant";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  buildSyncPreferredLanguageRequest,
  extractDealServicePreferredLanguage,
  getExplicitUserLanguage,
  getPreferredLanguage,
  hasExplicitUserLanguageSelection,
  hasPreferredLanguageDealSyncCompleted,
  markExplicitUserLanguageSelection,
  markPreferredLanguageDealSyncCompleted,
  toUserDbPreferredLanguage,
} from "utilities/getPreferredLanguage";
import {
  useGetBorrowerDetailsQuery,
  useGetBorrowerIdentifiersQuery,
  useGetBorrowerPersonalInformationQuery,
  useUpdateUserPreferredLanguageMutation,
} from "pages/dashboard/home/dashboardService";
import { useSyncPreferredLanguageMutation } from "pages/authentication/userService";
import { RegistrationRoleType } from "pages/registration/registrationService";

interface PreferredLanguageProps {
  readonly isMobile?: boolean;
  readonly isAuthenticated?: boolean;
}

const setLangPrefCookie = (langCode: string): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=${langCode};path=/`;
};

const clearLangPrefCookie = (): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const setLocalStorage = (key: string, value: string): void => {
  globalThis.localStorage.setItem(key, value);
};

const getLocalStorage = (key: string): string | null => {
  return globalThis.localStorage.getItem(key);
};

const refreshPageForTranslation = (): void => {
  globalThis.location.reload();
};

const shouldReloadForLanguageChange = (nextLanguage: string): boolean => {
  const currentLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
  return currentLanguage !== nextLanguage;
};

const isCoBorrowerRole = (userRole?: string): boolean => {
  return userRole === RegistrationRoleType.SpousalCoBorrower ||
    userRole === RegistrationRoleType.NonSpousalCoBorrower ||
    userRole === RegistrationRoleType.CoBorrower;
};

const PreferredLanguage: React.FC<PreferredLanguageProps> = ({ isMobile = false, isAuthenticated = false }) => {
  const dispatch = useDispatch();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const { pathname } = useLocation();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanId = useSelector(getLoanId);
  const userRole = useSelector(getUserRole);
  const dealBorrowerId = useSelector(getDealBorrowerId);
  const hasAcknowledgedSpanishPrompt = useSelector(getHasAcknowledgedSpanishPrompt);
  const hasPreferredLanguageOverride = useSelector(getHasPreferredLanguageOverride);
  const hasLoans = useSelector(selectHasLoans);
  const hasApplicationContext = Boolean(dealApplicationId);
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();
  const [syncPreferredLanguage] = useSyncPreferredLanguageMutation();
  const [showPostLosLockModal, setShowPostLosLockModal] = useState(false);
  const [isPersistingPreferredLanguage, setIsPersistingPreferredLanguage] = useState(false);
  const learnMoreButtonRef = useRef<HTMLButtonElement>(null);
  const postLoginSyncDoneRef = useRef(false);
  const currentLoanNumber = useSelector(getLoanNumber);
  const isAllPostLOS = useSelector(selectIsLanguageToggleLocked);
  const isMyLoansPage = pathname.startsWith("/my-loans");
  const isCurrentLoanPostLos = isPostLosLoan(currentLoanNumber);
  const isPostLosLoanContext = isMyLoansPage ? isAllPostLOS : isCurrentLoanPostLos;
  const isExplicitSelection = hasPreferredLanguageOverride || hasExplicitUserLanguageSelection();

  const { data: userProfile, isSuccess: isUserProfileSuccess } = useGetBorrowerDetailsQuery(userId, {
    skip: !userId,
  });
  const { data: borrowerIdentifiers } = useGetBorrowerIdentifiersQuery(
    { loanId, applicationId: dealApplicationId },
    { skip: !isAuthenticated || !hasApplicationContext || !loanId },
  );
  const resolvedDealBorrowerId = isCoBorrowerRole(userRole)
    ? borrowerIdentifiers?.coBorrowerId ?? dealBorrowerId
    : borrowerIdentifiers?.borrowerId ?? dealBorrowerId;
  const { data: dealServicePersonalInformation } = useGetBorrowerPersonalInformationQuery(
    { borrowerId: resolvedDealBorrowerId || "" },
    {
      skip:
        !isAuthenticated ||
        !hasApplicationContext ||
        !resolvedDealBorrowerId ||
        isExplicitSelection ||
        isPersistingPreferredLanguage,
    },
  );

  const [userLanguage, setUserLanguage] = useState<string>(() => getPreferredLanguage());
  const [showEnglishRevertModal, setShowEnglishRevertModal] = useState(false);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);

  useEffect(() => {
    if (isExplicitSelection) {
      const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY) ?? getExplicitUserLanguage();
      setUserLanguage(getPreferredLanguage(savedLanguage ?? reduxLanguage));
      return;
    }

    if (!isAuthenticated) {
      const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
      setUserLanguage(getPreferredLanguage(savedLanguage ?? reduxLanguage));
      return;
    }

    const dealServiceLanguage = extractDealServicePreferredLanguage(dealServicePersonalInformation);

    const hasLanguageSource = Boolean(reduxLanguage?.trim()) || Boolean(dealServiceLanguage);
    if (!hasLanguageSource) {
      const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
      setUserLanguage(getPreferredLanguage(savedLanguage ?? undefined));
      return;
    }

    const resolved = getPreferredLanguage(dealServiceLanguage?.trim() ? dealServiceLanguage : reduxLanguage?.trim());

    const currentUserDbLanguage = userProfile?.preferredLanguage;
    const isUserDbLanguageNull =
      isUserProfileSuccess &&
      (currentUserDbLanguage == null || currentUserDbLanguage.trim() === "");
    const shouldPromptSpanishAcknowledgment =
      isAuthenticated &&
      resolved === "Español" &&
      isUserDbLanguageNull &&
      !hasAcknowledgedSpanishPrompt &&
      !showAcknowledgmentModal;
    if (!shouldPromptSpanishAcknowledgment && resolved !== userLanguage) {
      const shouldReload = shouldReloadForLanguageChange(resolved);
      setUserLanguage(resolved);
      setLocalStorage(LOCAL_STORAGE_KEY, resolved);

      const response = updateUserPreferredLanguage({
        userId,
        preferredLanguage: toUserDbPreferredLanguage(resolved),
      });
      if (shouldReload) {
        refreshPageForTranslation();
        return;
      }
    }

    if (shouldPromptSpanishAcknowledgment) {
      setPendingLanguage(resolved);
      setShowAcknowledgmentModal(true);
    }
  }, [
    dealServicePersonalInformation,
    reduxLanguage,
    isAuthenticated,
    hasPreferredLanguageOverride,
    isExplicitSelection,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !userId || !hasExplicitUserLanguageSelection()) {
      return;
    }
    if (hasPreferredLanguageOverride || hasPreferredLanguageDealSyncCompleted()) {
      return;
    }
    if (postLoginSyncDoneRef.current) {
      return;
    }

    const savedLanguage = getExplicitUserLanguage();
    if (!savedLanguage) {
      return;
    }

    postLoginSyncDoneRef.current = true;
    dispatch(setPreferredLanguage(savedLanguage));
    dispatch(setHasPreferredLanguageOverride(true));

    const syncRequest = buildSyncPreferredLanguageRequest(userId, savedLanguage, {
      isMyLoansPage,
      nhDealApplicationId: nhDealApplicationId || undefined,
    });

    setIsPersistingPreferredLanguage(true);
    void syncPreferredLanguage(syncRequest)
      .then((response) => {
        if (!("error" in response)) {
          markPreferredLanguageDealSyncCompleted();
        }
      })
      .finally(() => {
        setIsPersistingPreferredLanguage(false);
      });
  }, [
    isAuthenticated,
    userId,
    isMyLoansPage,
    nhDealApplicationId,
    dispatch,
    syncPreferredLanguage,
    hasPreferredLanguageOverride,
  ]);

  useEffect(() => {
    const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
    if (savedLanguage === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  }, []);

  if (!featureFlag?.preferredLanguageEnabled) {
    return null;
  }

  const applyLanguageState = (value: string) => {
    setUserLanguage(value);
    setLocalStorage(LOCAL_STORAGE_KEY, value);

    if (isAuthenticated) {
      dispatch(setPreferredLanguage(value));
      dispatch(setHasPreferredLanguageOverride(true));
    }
    if (value === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  };

  const persistLanguageChange = async (value: string): Promise<boolean> => {
    if (!isAuthenticated || !userId) {
      return false;
    }

    const syncRequest = buildSyncPreferredLanguageRequest(userId, value, {
      isMyLoansPage,
      nhDealApplicationId: nhDealApplicationId || undefined,
    });

    setIsPersistingPreferredLanguage(true);
    try {
      const response = await syncPreferredLanguage(syncRequest);
      const didPersist = !("error" in response);
      if (didPersist) {
        markPreferredLanguageDealSyncCompleted();
      } else {
        setIsPersistingPreferredLanguage(false);
      }
      return didPersist;
    } catch {
      setIsPersistingPreferredLanguage(false);
      return false;
    }
  };

  const commitLanguageChange = async (value: string): Promise<void> => {
    markExplicitUserLanguageSelection();
    const shouldReload = shouldReloadForLanguageChange(value);
    applyLanguageState(value);

    if (!isAuthenticated) {
      if (shouldReload) {
        refreshPageForTranslation();
      }
      return;
    }

    postLoginSyncDoneRef.current = true;
    const didPersist = await persistLanguageChange(value);
    if (didPersist && shouldReload) {
      refreshPageForTranslation();
      return;
    }
    setIsPersistingPreferredLanguage(false);
  };

  const handleLanguageChange = (value: string) => {
    if (isPostLosLoanContext) {
      return;
    }
    setPendingLanguage(value);
    if (value == "Español") {
      setShowAcknowledgmentModal(true);
    } else {
      setShowEnglishRevertModal(true);
    }
  };

  const handleAcknowledge = () => {
    const languageToApply = pendingLanguage ?? "Español";
    setPendingLanguage(null);
    if (isAuthenticated) {
      dispatch(setHasAcknowledgedSpanishPrompt(true));
    }
    setShowAcknowledgmentModal(false);
    void commitLanguageChange(languageToApply);
  };

  const handleRevertConfirm = () => {
    const languageToApply = pendingLanguage ?? DEFAULT_LANGUAGE;
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
    void commitLanguageChange(languageToApply);
  };

  const handleRevertCancel = () => {
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
  };
  const handleClosePostLosLockModal = () => {
    setShowPostLosLockModal(false);
  };

  const isDisabled = isAuthenticated ? !!hasLoans : false;

  return (
    <>
      <div className="preferred-language">
        {isPostLosLoanContext && !isMobile && (
          <div className="preferred-language__lock-details">
            <span className="preferred-language__lock-label">Language Locked</span>
            <button
              ref={learnMoreButtonRef}
              type="button"
              className="preferred-language__learn-more-button"
              onClick={() => setShowPostLosLockModal(true)}
            >
              Learn More
            </button>
          </div>
        )}
        <LanguageToggle
          value={userLanguage}
          onChange={handleLanguageChange}
          options={SUPPORTED_LANGUAGES}
          isMobile={isMobile}
          disabled={isPostLosLoanContext || isPersistingPreferredLanguage}
          onLockedClick={isMobile && isPostLosLoanContext ? () => setShowPostLosLockModal(true) : undefined}
        />

      </div>
      {isAuthenticated && isPersistingPreferredLanguage && (
        <LoadingOverlay text="Updating your language preference..." />
      )}
      {showAcknowledgmentModal && (
        <SpanishAcknowledgmentModal
          onAcknowledge={handleAcknowledge}
          onCancel={() => {
            setShowAcknowledgmentModal(false);
            setPendingLanguage(null);
          }}
        />
      )}
      {showEnglishRevertModal && (
        <EnglishRevertModal onConfirm={handleRevertConfirm} onCancel={handleRevertCancel} />
      )}
      {showPostLosLockModal && (
        <PostLosLockModal onClose={handleClosePostLosLockModal} returnFocusRef={learnMoreButtonRef} />
      )}
    </>
  );
};

export default PreferredLanguage;
